import React, { useEffect, useState } from 'react';
import { GoogleMap, MarkerF } from '@react-google-maps/api';
import { useGoogleMaps } from 'context/GoogleContext';
import { calculateDistance } from 'services/distanceHelper';
import PartnerInfoPanel from './PartnerInfoPanel';
import CustomPartnerMarker from './CustomPartnerMarker';
import PartnerSeal from 'assets/images/e3dc_siegel_partner.png';
import PremiumPartnerSeal from 'assets/images/e3dc_siegel_premium.png';
import AlliancePartnerSeal from 'assets/images/e3dc_siegel_alliance.png';
import MapMarker from 'assets/images/maps-marker.svg';

const DEFAULT_CENTER = { lat: 51.165691, lng: 10.451526 };
const DEFAULT_ZOOM = 6;
const MAX_RADIUS_KM = 200;
const MAX_NEARBY_PARTNERS = 10;

const MapStylesGreen = [
  {
    featureType: 'administrative',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#444444'
      }
    ]
  },
  {
    featureType: 'landscape',
    elementType: 'all',
    stylers: [
      {
        color: '#f2f2f2'
      }
    ]
  },
  {
    featureType: 'poi',
    elementType: 'all',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'road',
    elementType: 'all',
    stylers: [
      {
        saturation: -100
      },
      {
        lightness: 45
      }
    ]
  },
  {
    featureType: 'road.highway',
    elementType: 'all',
    stylers: [
      {
        visibility: 'simplified'
      }
    ]
  },
  {
    featureType: 'road.arterial',
    elementType: 'labels.icon',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'transit',
    elementType: 'all',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'water',
    elementType: 'all',
    stylers: [
      {
        color: '#000000'
      },
      {
        visibility: 'on'
      }
    ]
  },
  {
    featureType: 'water',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#19290C'
      }
    ]
  },
  {
    featureType: 'water',
    elementType: 'geometry.stroke',
    stylers: [
      {
        color: '#19290C'
      }
    ]
  }
];

const MapContainerStyle = {
  height: '430px',
  width: '100%',
  borderRadius: '8px'
};

const getPartnerSeal = (partner) => {
  if (partner.partnerType === 2) {
    return AlliancePartnerSeal;
  }
  if (partner.partnerType === 1) {
    return PremiumPartnerSeal;
  }
  return PartnerSeal;
};

const PartnerResultItem = ({ partner, isSelected, onClick }) => (
  <button
    type="button"
    className={isSelected ? 'partner-result-item active' : 'partner-result-item'}
    onClick={() => onClick(partner)}
  >
    <img className="partner-result-item-seal" src={getPartnerSeal(partner)} alt="E3/DC Partner" />
    <span className="partner-result-item-body">
      <span className="partner-result-item-header">
        <span className="partner-result-item-name">{partner.name || 'Unbekannter Partner'}</span>
        <span className="partner-result-item-distance">
          <svg className="partner-result-item-distance-icon" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" viewBox="0 0 24 24"><path stroke="none" d="M0 0h24v24H0z"></path><path d="M3 11a1 1 0 1 1-1 1.013 1 1 0 0 1 1-1zM21 11a1 1 0 1 1-1 1.013 1 1 0 0 1 1-1zM8 12h8M13 9l3 3-3 3"></path></svg>
          {partner.distance.toFixed(1)} km
        </span>
      </span>
      {(partner.street || partner.zipcode || partner.city) && (
        <span className="partner-result-item-address">
          {[partner.street, [partner.zipcode, partner.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
        </span>
      )}
      {/* <span className="partner-result-item-services">
        {partner.hasPvSpeicher && <span>PV & Speicher</span>}
        {partner.hasEMobility && <span>E-Mobilität</span>}
        {partner.hasSmartMeter && <span>Smart Meter</span>}
        {partner.hasWaermepumpeInstall && <span>Wärmepumpe</span>}
        {partner.hasShowroom && <span>Showroom</span>}
      </span> */}
    </span>
  </button>
);

// Main component that uses GoogleMapsProvider and context
const PartnerMapContent = ({ allPartners, isLoadingPartners = false, userLatLng, onPartnerSelect, selectedPartner, disabled = false }) => {
  const { google, isLoaded: isMapsLoaded } = useGoogleMaps();
  const [map, setMap] = useState(null);
  const [userMarkerPosition, setUserMarkerPosition] = useState(null);
  const [nearbyPartners, setNearbyPartners] = useState([]);
  const [showNoPartners, setShowNoPartners] = useState(false);

  // Calculate distance from user to partner
  const getDistance = (partner) => {
    if (!userLatLng || !partner.lat || !partner.lng) return null;
    return calculateDistance(
      userLatLng.lat,
      userLatLng.lng,
      partner.lat,
      partner.lng
    );
  };

  // Filter partners by distance when data changes
  useEffect(() => {
    if (!userLatLng || !allPartners) {
      setNearbyPartners([]);
      setShowNoPartners(false);
      return;
    }

    const filtered = allPartners
      .map(partner => ({
        ...partner,
        distance: getDistance(partner)
      }))
      .filter(partner => partner.distance !== null && partner.distance <= MAX_RADIUS_KM)
      .sort((a, b) => a.distance - b.distance)
      .slice(0, MAX_NEARBY_PARTNERS);

    setNearbyPartners(filtered);
    setShowNoPartners(!isLoadingPartners && allPartners.length > 0 && filtered.length === 0);
  }, [allPartners, userLatLng, isLoadingPartners]);

  // Set user marker position and center map when userLatLng changes
  useEffect(() => {
    if (userLatLng && userLatLng.lat && userLatLng.lng) {
      setUserMarkerPosition(userLatLng);
      
      if (map) {
        map.panTo(userLatLng);
        map.setZoom(8);
      }
    }
  }, [userLatLng, map]);

  // Pan/zoom map when nearby partners change and user has no selection
  useEffect(() => {
    if (map && google && nearbyPartners.length > 0 && !selectedPartner) {
      const bounds = new google.maps.LatLngBounds();
      nearbyPartners.forEach(partner => {
        bounds.extend({ lat: partner.lat, lng: partner.lng });
      });
      map.fitBounds(bounds);
    }
  }, [nearbyPartners, selectedPartner, map, google]);

  const selectPartner = (partner) => {
    onPartnerSelect(partner);
    if (map) {
      map.panTo({ lat: partner.lat, lng: partner.lng });
      map.setZoom(10);
    }
  };

  const handleMarkerClick = (partner) => {
    if (selectedPartner && selectedPartner.id === partner.id) {
      onPartnerSelect(null);
    } else {
      selectPartner(partner);
    }
  };

  const handleUserMarkerClick = () => {
    if (map) {
      map.panTo(userLatLng);
      map.setZoom(10);
    }
  };

  return (
    <>
      <div className="partner-map-with-results">
        <aside className="partner-result-list" aria-label="Partner in Ihrer Nähe">
          <div className="partner-result-list-head">
            <span className="partner-result-list-title">Partner in Ihrer Nähe</span>
            <span className="partner-result-list-count">{nearbyPartners.length}</span>
          </div>
          {nearbyPartners.length > 0 ? (
            <div className="partner-result-list-items">
              {nearbyPartners.map((partner) => (
                <PartnerResultItem
                  key={partner.id}
                  partner={partner}
                  isSelected={selectedPartner && selectedPartner.id === partner.id}
                  onClick={selectPartner}
                />
              ))}
            </div>
          ) : isLoadingPartners ? (
            <div className="partner-result-list-loading">
              <div className="spinner-border text-e3dc" role="status">
                <span className="visually-hidden">Laden...</span>
              </div>
            </div>
          ) : (
            <div className="partner-result-list-empty">
              <strong>Keine Partner gefunden</strong>
              <span>Die Suche wurde auf einen Radius von {MAX_RADIUS_KM} km beschränkt.</span>
            </div>
          )}
          {disabled && <div className="partner-selection-overlay" aria-hidden="true" />}
        </aside>

        <div className="partner-map-section">
          {showNoPartners && (
            <div className="partner-map-empty">
              <p className="empty-text">Leider keine Partner in Ihrer Nähe gefunden.</p>
              <p className="empty-subtext">Die Suche wurde auf einen Radius von {MAX_RADIUS_KM} km beschränkt.</p>
            </div>
          )}
          {!isMapsLoaded ? (
            <div className="partner-map-placeholder" aria-hidden="true" />
          ) : (
            <GoogleMap
              mapContainerStyle={MapContainerStyle}
              center={userMarkerPosition || DEFAULT_CENTER}
              zoom={userMarkerPosition ? 8 : DEFAULT_ZOOM}
              options={{
                styles: MapStylesGreen,
                disableDefaultUI: true,
                gestureHandling: 'none',
                draggable: false,
                keyboardShortcuts: false,
                clickableIcons: false,
                zoomControl: false,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false
              }}
              onLoad={setMap}
            >
              {/* User location marker */}
              {userMarkerPosition && (
                <MarkerF
                  position={userMarkerPosition}
                  icon={{
                    url: MapMarker,
                    scaledSize: new window.google.maps.Size(38, 46),
                    anchor: new window.google.maps.Point(19, 46)
                  }}
                  onClick={handleUserMarkerClick}
                  clickable={true}
                />
              )}

              {/* Partner markers */}
              {nearbyPartners.map((partner) => (
                <CustomPartnerMarker
                  key={partner.id}
                  location={partner}
                  isSelected={selectedPartner && selectedPartner.id === partner.id}
                  onMarkerClick={handleMarkerClick}
                />
              ))}
            </GoogleMap>
          )}
          {disabled && <div className="partner-selection-overlay" aria-hidden="true" />}
        </div>
      </div>

      {/* Partner info panel */}
      {/* {selectedPartner && (
        <div className="partner-info-container">
          <PartnerInfoPanel
            partner={selectedPartner}
            distance={selectedPartner.distance}
            onClose={() => onPartnerSelect(null)}
          />
        </div>
      )} */}
    </>
  );
};

// Wrapper that provides GoogleMaps context
const PartnerMap = (props) => {
  return (
    <PartnerMapContent {...props} />
  );
};

export default PartnerMap;
